import { useAuth } from "@dl/web-core-js/auth";
import { Button, cn, Dialog, Field, Input } from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { Minus, Plus } from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ShopFloorClient, type MaterialUse, type StepMaterialDTO } from "../../api/floor";
import { formatNumber } from "../../api/manufacturing";

/** Raw stock counted at the station, not yet sent: material line id to quantity (never below zero). */
export type PendingCounts = Record<number, number>;

/** Pending counts as the report's or the count's `materials_used`. */
export function materialsUsed(pending: PendingCounts): MaterialUse[] {
  return Object.entries(pending)
    .filter(([, quantity]) => quantity > 0)
    .map(([id, quantity]) => ({ production_order_material_id: Number(id), quantity }));
}

/** The query key of a step's materials, refreshed after a report or a count. */
export const stepMaterialsKey = (operationId: number) => ["production", "shop-floor", "step-materials", operationId];

/** A step's materials, shared by the panel and the kiosk (which checks a count against the estimate before sending it). */
export function useStepMaterials(operationId: number | null) {
  const { http } = useAuth();
  const floor = useMemo(() => new ShopFloorClient(http), [http]);
  return useQuery({
    queryKey: stepMaterialsKey(operationId ?? 0),
    queryFn: () => floor.materials(operationId!),
    enabled: operationId != null,
  });
}

/** The counted lines that the pending counts take past their estimate. */
export function overEstimate(rows: StepMaterialDTO[], pending: PendingCounts): StepMaterialDTO[] {
  return rows.filter((row) => {
    const adding = pending[row.production_order_material_id] ?? 0;
    return row.is_countable && adding > 0 && Number(row.quantity_counted ?? 0) + adding > Number(row.quantity_required);
  });
}

/**
 * What a step uses, at its station.
 *
 * <ul>
 *   <li><b>For one unit</b>: what the bill says one unit takes at this step
 *       (the kit's hardware, the cornières, the pads, the cartons). Nothing to
 *       count: each unit reported uses its share, and the line says how much
 *       has been taken so far.</li>
 *   <li><b>Counted here</b>: the raw stock the step cuts from, where the
 *       process counts it (the panels at Laval's saw, the foam sheets at
 *       Terrebonne). The operator adds what was pulled; it goes with the next
 *       report, or alone with Count, and consumption follows the count. A
 *       count never goes below zero (a count is corrected on the order's
 *       material log), and one past the estimate asks why.</li>
 * </ul>
 *
 * Renders nothing when the step uses nothing.
 */
export function PanelsUsed({
  operationId,
  processCode,
  pending,
  onChange,
  onCount,
  busy,
}: {
  operationId: number;
  /** FOAM counts sheets; anything else counted is panels. */
  processCode: string | null | undefined;
  pending: PendingCounts;
  onChange: (next: PendingCounts) => void;
  onCount: () => void;
  busy: boolean;
}) {
  const { t } = useTranslation();
  const materials = useStepMaterials(operationId);
  const rows = materials.data ?? [];
  if (rows.length === 0) return null;
  const counted = rows.filter((row) => row.is_countable);
  const perUnit = rows.filter((row) => !row.is_countable && Number(row.quantity_per_unit) > 0);
  const over = overEstimate(counted, pending);

  const change = (row: StepMaterialDTO, next: number) => {
    const rounded = row.is_consumed_whole ? Math.round(next) : Math.round(next * 100) / 100;
    onChange({ ...pending, [row.production_order_material_id]: Math.max(0, rounded) });
  };
  const waiting = materialsUsed(pending).length > 0;
  const sheets = processCode === "FOAM";

  return (
    <>
      {perUnit.length > 0 && (
        <section className="mt-6 rounded-xl border border-line bg-surface-sunk p-4">
          <div className="mb-3 flex flex-wrap items-baseline gap-x-3">
            <h3 className="text-[20px] font-semibold">{t("kiosk.unit.title", "For one unit")}</h3>
            <span className="text-[14px] text-ink-muted">{t("kiosk.unit.hint", "Each unit you report uses this; nothing to count.")}</span>
          </div>
          <ul className="grid gap-2 sm:grid-cols-2">
            {perUnit.map((row) => (
              <li key={row.production_order_material_id} className="flex items-center gap-3 rounded-lg bg-surface px-4 py-2.5">
                <span className="min-w-[4rem] text-right text-[24px] font-bold tabular-nums">
                  {formatNumber(row.quantity_per_unit)}
                  <span className="ml-1 text-[13px] font-medium text-ink-muted">{row.unit_code}</span>
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[16px] font-semibold">{row.part_number}</span>
                  <span className="block truncate text-[13px] text-ink-muted">{row.part_name}</span>
                </span>
                {!row.is_made && (
                  <span className="shrink-0 text-right text-[12px] tabular-nums text-ink-muted">
                    {t("kiosk.unit.taken", "{{taken}} of {{needed}} used", {
                      taken: formatNumber(row.quantity_consumed),
                      needed: formatNumber(row.quantity_required),
                    })}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {counted.length > 0 && (
        <section className="mt-6 rounded-xl border border-line bg-surface-sunk p-4">
          <div className="mb-3 flex flex-wrap items-baseline gap-x-3">
            <h3 className="text-[20px] font-semibold">
              {sheets ? t("kiosk.sheets.title", "Foam sheets used") : t("kiosk.panels.title", "Panels used")}
            </h3>
            <span className="text-[14px] text-ink-muted">
              {sheets
                ? t("kiosk.sheets.hint", "Add the sheets you pulled. They go with your next report, or press Count.")
                : t("kiosk.panels.hint", "Add the panels you pulled. They go with your next report, or press Count.")}
            </span>
          </div>
          <ul className="flex flex-col gap-3">
            {counted.map((row) => {
              const value = pending[row.production_order_material_id] ?? 0;
              const step = row.is_consumed_whole ? 1 : 0.5;
              const after = Number(row.quantity_counted ?? 0) + value;
              const past = value > 0 && after > Number(row.quantity_required);
              return (
                <li key={row.production_order_material_id} className="flex flex-wrap items-center gap-4 rounded-lg bg-surface px-4 py-3">
                  <div className="min-w-[12rem] flex-1">
                    <p className="text-[18px] font-semibold">{row.part_number}</p>
                    <p className="text-[14px] text-ink-muted">{row.part_name}</p>
                    <p className="mt-1 text-[14px] tabular-nums text-ink-muted">
                      {t("kiosk.panels.figures", "Estimate {{estimate}} · counted {{counted}} · taken from stock {{consumed}}", {
                        estimate: formatNumber(row.quantity_required),
                        counted: row.quantity_counted == null ? t("kiosk.panels.never", "not yet") : formatNumber(row.quantity_counted),
                        consumed: formatNumber(row.quantity_consumed),
                      })}
                    </p>
                    {past && (
                      <p className="mt-1 text-[14px] font-medium text-warn">
                        {t("kiosk.panels.past", "{{after}} counted, past the estimate of {{estimate}}: you will be asked why.", {
                          after: formatNumber(after),
                          estimate: formatNumber(row.quantity_required),
                        })}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      aria-label={t("kiosk.panels.less", "One less")}
                      disabled={busy || value <= 0}
                      onClick={() => change(row, value - step)}
                      className="flex size-14 items-center justify-center rounded-lg border border-line bg-surface hover:bg-surface-sunk disabled:opacity-30"
                    >
                      <Minus className="size-6" />
                    </button>
                    <span className={cn("min-w-[4.5rem] text-center text-[28px] font-bold tabular-nums", past ? "text-warn" : value > 0 ? "text-accent" : "text-ink-muted")}>
                      {value > 0 ? `+${formatNumber(value)}` : formatNumber(value)}
                    </span>
                    <button
                      type="button"
                      aria-label={t("kiosk.panels.more", "One more")}
                      disabled={busy}
                      onClick={() => change(row, value + step)}
                      className="flex size-14 items-center justify-center rounded-lg border border-line bg-surface hover:bg-surface-sunk disabled:opacity-50"
                    >
                      <Plus className="size-6" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button
              type="button"
              disabled={!waiting || busy}
              onClick={onCount}
              className="min-h-14 rounded-xl border-2 border-accent bg-accent px-6 text-[18px] font-semibold text-ink-on-accent hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {t("kiosk.panels.count", "Count")}
            </button>
            {waiting && (
              <button type="button" disabled={busy} onClick={() => onChange({})} className="text-[15px] text-ink-muted underline">
                {t("kiosk.panels.clear", "Clear")}
              </button>
            )}
            {over.length > 0 && <span className="text-[14px] text-warn">{t("kiosk.panels.past-count", "{{n}} past the estimate", { n: over.length })}</span>}
          </div>
        </section>
      )}
    </>
  );
}

/**
 * Asks why a count goes past its estimate (a damaged sheet, a recut), before
 * it is sent. `ask` settles with the reason, or null when cancelled.
 */
export function useCountReasonPrompt(): { ask: (over: StepMaterialDTO[]) => Promise<string | null>; element: ReactNode } {
  const [pending, setPending] = useState<{ over: StepMaterialDTO[]; resolve: (reason: string | null) => void } | null>(null);
  const ask = (over: StepMaterialDTO[]) => new Promise<string | null>((resolve) => setPending({ over, resolve }));
  const settle = (reason: string | null) => {
    pending?.resolve(reason);
    setPending(null);
  };
  const element = pending ? <CountReasonDialog over={pending.over} onCancel={() => settle(null)} onSubmit={settle} /> : null;
  return { ask, element };
}

function CountReasonDialog({ over, onCancel, onSubmit }: { over: StepMaterialDTO[]; onCancel: () => void; onSubmit: (reason: string) => void }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  return (
    <Dialog
      open
      onClose={onCancel}
      title={t("kiosk.panels.why.title", "Past the estimate")}
      description={over.map((row) => `${row.part_number}: ${t("kiosk.panels.why.line", "estimate {{n}}", { n: formatNumber(row.quantity_required) })}`).join(" · ")}
      actions={
        <>
          <Button variant="ghost" onClick={onCancel}>
            {t("dialog.cancel", "Cancel")}
          </Button>
          <Button variant="primary" disabled={reason.trim() === ""} onClick={() => onSubmit(reason.trim())}>
            {t("kiosk.panels.why.record", "Record it")}
          </Button>
        </>
      }
    >
      <Field label={t("override.reason", "Why")} htmlFor="count-why" required hint={t("kiosk.panels.why.hint", "A damaged sheet, a recut, a miscount before.")}>
        <Input id="count-why" autoFocus value={reason} onChange={(event) => setReason(event.target.value)} />
      </Field>
    </Dialog>
  );
}
